/**
 * QRDocumentWithComments
 *
 * Renders a QuestionnaireResponse as a flat, continuous document with
 * per-item inline comments. Replaces the plain-text DocumentWithLineComments
 * for QR-backed document review.
 *
 * Rendering:
 * - Leaf items use ResponseItemReadOnly from the Appointment page (same
 *   visual output, same helpers from fhirNoteMapping).
 * - Group items render a section header and recurse children, adding
 *   comment anchors at every level.
 * - Multi-page questionnaires are flattened: all items render sequentially
 *   with no pagination or navigation controls.
 *
 * Comment anchoring:
 * - Each item is wrapped with data-anchor-link-id and data-anchor-path
 *   attributes derived from Questionnaire.item.linkId + nested path.
 * - Ensures deterministic, stable anchors that survive item reordering.
 */

import type {
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import {
  Alert,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
  IconMessageCirclePlus,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DocumentComment, DocumentCommentAnchor } from '../types';
import { ResponseItemReadOnly } from '../../appointments/ProgressNoteCard';
import {
  findQuestionnaireItemText,
  getItemAnswerText,
} from '../../appointments/fhirNoteMapping';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
}

/** Build a unique slash-joined anchor path for a QR item in the tree. */
function buildAnchorPath(
  linkId: string | undefined,
  parentPath: string,
): string {
  const segment = linkId ?? '';
  return parentPath ? `${parentPath}/${segment}` : segment;
}

const EMPTY_PLACEHOLDER = '\u2014';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface QRDocumentWithCommentsProps {
  questionnaireResponse: QuestionnaireResponse | null;
  questionnaire: Questionnaire | null;
  comments: DocumentComment[];
  onAddComment: (anchor: DocumentCommentAnchor, text: string) => void;
  onResolveComment?: (commentId: string) => void;
  isSigned: boolean;
  /** When true, hide add-comment and resolve actions (e.g. appointment view). */
  readOnly?: boolean;
  /** When true and readOnly, still show resolve button (e.g. clinician resolving from appointment page). */
  allowResolveInReadOnly?: boolean;
  /** When true, do not render the "Document" section heading (e.g. when embedded in ProgressNoteCard). */
  hideTitle?: boolean;
  /** When true, show question numbers on answer blocks only (one per question; section/question titles have no number). */
  showLineNumbers?: boolean;
  loading?: boolean;
  error?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QRDocumentWithComments({
  questionnaireResponse,
  questionnaire,
  comments,
  onAddComment,
  onResolveComment,
  isSigned,
  readOnly = false,
  allowResolveInReadOnly = false,
  hideTitle = false,
  showLineNumbers = false,
  loading,
  error,
}: QRDocumentWithCommentsProps): JSX.Element {
  const canResolve = !isSigned && (onResolveComment != null) && (!readOnly || allowResolveInReadOnly);
  const [replyItemPath, setReplyItemPath] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const [hoveredItemPath, setHoveredItemPath] = useState<string | null>(null);
  const [expandedResolvedPaths, setExpandedResolvedPaths] = useState<
    Set<string>
  >(new Set());

  // Focus textarea when reply opens
  useEffect(() => {
    if (replyItemPath != null) {
      const timer = setTimeout(() => commentInputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [replyItemPath]);

  // -----------------------------------------------------------------------
  // Comment lookup by anchor path
  // -----------------------------------------------------------------------

  const pathCommentsMap = useMemo(() => {
    const map: Record<string, DocumentComment[]> = {};
    comments.forEach((c) => {
      const key = c.anchor?.itemPath ?? c.anchor?.linkId;
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(c);
    });
    return map;
  }, [comments]);

  // -----------------------------------------------------------------------
  // Interaction handlers
  // -----------------------------------------------------------------------

  const toggleResolvedExpanded = useCallback((path: string) => {
    setExpandedResolvedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const handleOpenReply = useCallback(
    (itemPath: string) => {
      if (isSigned) return;
      setReplyItemPath(itemPath);
      setReplyText('');
    },
    [isSigned],
  );

  const handleSubmitReply = useCallback(
    (itemPath: string, linkId: string, snippetText: string) => {
      if (!replyText.trim()) return;
      onAddComment(
        {
          linkId,
          itemPath,
          snippet: snippetText.slice(0, 80) || undefined,
        },
        replyText.trim(),
      );
      setReplyItemPath(null);
      setReplyText('');
    },
    [replyText, onAddComment],
  );

  const handleCancelReply = useCallback(() => {
    setReplyItemPath(null);
    setReplyText('');
  }, []);

  // -----------------------------------------------------------------------
  // Thread rendering (same UI pattern as DocumentWithLineComments)
  // -----------------------------------------------------------------------

  const renderThread = (
    itemPath: string,
    threadComments: DocumentComment[],
  ) => {
    const byCreated = (a: DocumentComment, b: DocumentComment) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    const unresolved = threadComments
      .filter((c) => !c.resolvedAt)
      .sort(byCreated);
    const resolved = threadComments
      .filter((c) => c.resolvedAt)
      .sort(byCreated);
    const hasResolved = resolved.length > 0;
    const isResolvedExpanded = expandedResolvedPaths.has(itemPath);

    const commentRow = (c: DocumentComment, isResolved: boolean) => (
      <Box
        key={c.id}
        p="xs"
        style={{
          backgroundColor: isResolved
            ? 'var(--mantine-color-gray-0)'
            : 'var(--mantine-color-blue-0)',
          borderRadius: 8,
          borderLeft: isResolved
            ? undefined
            : '3px solid var(--mantine-color-blue-5)',
        }}
      >
        <Group justify="space-between" mb={4} wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" fw={600}>
              {c.authorName}
            </Text>
            <Text size="xs" c="gray.6">
              {new Date(c.createdAt).toLocaleString()}
            </Text>
            {isResolved && (
              <Badge size="xs" color="green" variant="light">
                Resolved
              </Badge>
            )}
          </Group>
          {!isResolved && canResolve && (
            <Button
              size="xs"
              variant="subtle"
              color="green"
              onClick={() => onResolveComment?.(c.id)}
            >
              Resolve
            </Button>
          )}
        </Group>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {c.text}
        </Text>
      </Box>
    );

    return (
      <Box mt="xs">
        <Stack gap="xs">
          {unresolved.map((c) => commentRow(c, false))}
        </Stack>
        {hasResolved && (
          <>
            <UnstyledButton
              onClick={() => toggleResolvedExpanded(itemPath)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 8px',
                borderRadius: 6,
                backgroundColor: 'var(--mantine-color-gray-0)',
                width: '100%',
                textAlign: 'left',
                marginTop: unresolved.length > 0 ? 8 : 0,
              }}
            >
              {isResolvedExpanded ? (
                <IconChevronDown
                  size={14}
                  color="var(--mantine-color-gray-6)"
                />
              ) : (
                <IconChevronRight
                  size={14}
                  color="var(--mantine-color-gray-6)"
                />
              )}
              <Text size="xs" c="gray.7" fw={500}>
                Resolved · {resolved.length} comment
                {resolved.length !== 1 ? 's' : ''}
              </Text>
            </UnstyledButton>
            <Collapse in={isResolvedExpanded}>
              <Stack gap="xs" mt="xs">
                {resolved.map((c) => commentRow(c, true))}
              </Stack>
            </Collapse>
          </>
        )}
      </Box>
    );
  };

  // -----------------------------------------------------------------------
  // Question number: only for answer blocks (not section/question titles), resets per render
  // -----------------------------------------------------------------------
  const questionNumberRef = useRef(0);

  const emptyGutter = () =>
    showLineNumbers ? (
      <Box style={{ width: 36, flexShrink: 0 }} />
    ) : null;

  // -----------------------------------------------------------------------
  // Recursive QR item rendering with comment anchors
  // -----------------------------------------------------------------------

  const renderQRItem = (
    item: QuestionnaireResponseItem,
    parentPath: string,
    index: number,
  ): JSX.Element => {
    const linkId = item.linkId ?? '';
    const itemPath = buildAnchorPath(linkId, parentPath);
    const hasChildren = item.item && item.item.length > 0;

    const title =
      item.text ??
      (questionnaire && linkId
        ? findQuestionnaireItemText(questionnaire, linkId)
        : undefined) ??
      '';

    const pathComments = pathCommentsMap[itemPath] ?? [];
    const hasComments = pathComments.length > 0;
    const isHovered = hoveredItemPath === itemPath;
    const isReply = replyItemPath === itemPath;

    if (showLineNumbers) {
      // Question numbers only on answer blocks (one per leaf item); section/question titles have no number
      const rows: JSX.Element[] = [];
      if (hasChildren) {
        if (title) {
          rows.push(
            <Group key="title" gap="xs" wrap="nowrap" align="flex-start" style={{ padding: '2px 0' }}>
              {emptyGutter()}
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} size="sm" c="gray.8">
                  {title}
                </Text>
              </Box>
            </Group>,
          );
        }
      } else {
        if (title) {
          rows.push(
            <Group key="title" gap="xs" wrap="nowrap" align="flex-start" style={{ padding: '2px 0' }}>
              {emptyGutter()}
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} size="sm" c="gray.8" mb={4}>
                  {title}
                </Text>
              </Box>
            </Group>,
          );
        }
        const answerText = getItemAnswerText(item);
        questionNumberRef.current += 1;
        const currentQuestionNum = questionNumberRef.current;
        const answerGutter = (
          <Box
            style={{
              flexShrink: 0,
              width: 36,
              textAlign: 'right',
              color: 'var(--mantine-color-gray-5)',
              userSelect: 'none',
              lineHeight: 1.55,
              fontSize: 'var(--mantine-font-size-sm)',
            }}
          >
            {currentQuestionNum}
          </Box>
        );
        if (answerText) {
          rows.push(
            <Group key="answer" gap="xs" wrap="nowrap" align="flex-start" style={{ padding: '2px 0' }}>
              {answerGutter}
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" c="gray.8" mb={2} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                  {stripHtml(answerText) || EMPTY_PLACEHOLDER}
                </Text>
              </Box>
            </Group>,
          );
        } else {
          rows.push(
            <Group key="empty" gap="xs" wrap="nowrap" align="flex-start" style={{ padding: '2px 0' }}>
              {answerGutter}
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" c="gray.5">
                  {EMPTY_PLACEHOLDER}
                </Text>
              </Box>
            </Group>,
          );
        }
      }

      return (
        <Box
          key={linkId || `item-${index}`}
          data-anchor-link-id={linkId}
          data-anchor-path={itemPath}
          onMouseEnter={() => setHoveredItemPath(itemPath)}
          onMouseLeave={() =>
            setHoveredItemPath((prev) => (prev === itemPath ? null : prev))
          }
          style={{ padding: '4px 0' }}
        >
          <Stack gap={0}>
            {rows}
          </Stack>
          {!hasChildren && (
            <Group gap="xs" wrap="nowrap" align="flex-start" style={{ marginTop: 4 }}>
              {emptyGutter()}
              <Box style={{ flex: 1, minWidth: 0 }}>
                {isHovered && !isSigned && !readOnly && (
                  <UnstyledButton
                    onClick={() => handleOpenReply(itemPath)}
                    style={{ padding: 2, borderRadius: 4 }}
                    aria-label={`Add comment on ${title || linkId}`}
                  >
                    <IconMessageCirclePlus size={14} color="var(--mantine-color-gray-5)" />
                  </UnstyledButton>
                )}
                {hasComments && (
                  <Badge size="xs" variant="light" color="blue">
                    {pathComments.length}
                  </Badge>
                )}
              </Box>
            </Group>
          )}
          {isReply && (
            <Paper p="xs" withBorder mt="xs" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <Textarea
                ref={commentInputRef}
                placeholder="Add a comment (request edits)..."
                value={replyText}
                onChange={(e) => setReplyText(e.currentTarget.value)}
                minRows={2}
                size="xs"
                autosize
              />
              <Group gap="xs" mt="xs">
                <Button size="xs" variant="outline" onClick={handleCancelReply}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  onClick={() => handleSubmitReply(itemPath, linkId, title)}
                  disabled={!replyText.trim()}
                >
                  Add comment
                </Button>
              </Group>
            </Paper>
          )}
          {hasComments && renderThread(itemPath, pathComments)}
          {hasChildren && (
            <Stack gap="xs" pl="sm" mt={4}>
              {item.item!.map((child, idx) =>
                renderQRItem(child, itemPath, idx),
              )}
            </Stack>
          )}
        </Box>
      );
    }

    return (
      <Box
        key={linkId || `item-${index}`}
        data-anchor-link-id={linkId}
        data-anchor-path={itemPath}
        onMouseEnter={() => setHoveredItemPath(itemPath)}
        onMouseLeave={() =>
          setHoveredItemPath((prev) => (prev === itemPath ? null : prev))
        }
        style={{ padding: '4px 0' }}
      >
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Box style={{ flex: 1, minWidth: 0 }}>
            {hasChildren ? (
              title ? (
                <Text fw={600} size="sm" c="gray.8">
                  {title}
                </Text>
              ) : null
            ) : (
              <ResponseItemReadOnly
                item={item}
                questionnaire={questionnaire}
              />
            )}
          </Box>

          {isHovered && !isSigned && !readOnly && (
            <UnstyledButton
              onClick={() => handleOpenReply(itemPath)}
              style={{ padding: 2, borderRadius: 4, flexShrink: 0 }}
              aria-label={`Add comment on ${title || linkId}`}
            >
              <IconMessageCirclePlus
                size={14}
                color="var(--mantine-color-gray-5)"
              />
            </UnstyledButton>
          )}

          {hasComments && (
            <Badge
              size="xs"
              variant="light"
              color="blue"
              style={{ flexShrink: 0 }}
            >
              {pathComments.length}
            </Badge>
          )}
        </Group>

        {/* Reply form */}
        {isReply && (
          <Paper
            p="xs"
            withBorder
            mt="xs"
            style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
          >
            <Textarea
              ref={commentInputRef}
              placeholder="Add a comment (request edits)..."
              value={replyText}
              onChange={(e) => setReplyText(e.currentTarget.value)}
              minRows={2}
              size="xs"
              autosize
            />
            <Group gap="xs" mt="xs">
              <Button size="xs" variant="outline" onClick={handleCancelReply}>
                Cancel
              </Button>
              <Button
                size="xs"
                onClick={() => handleSubmitReply(itemPath, linkId, title)}
                disabled={!replyText.trim()}
              >
                Add comment
              </Button>
            </Group>
          </Paper>
        )}

        {/* Comment threads */}
        {hasComments && renderThread(itemPath, pathComments)}

        {/* Group children (recurse) */}
        {hasChildren && (
          <Stack gap="xs" pl="sm" mt={4}>
            {item.item!.map((child, idx) =>
              renderQRItem(child, itemPath, idx),
            )}
          </Stack>
        )}
      </Box>
    );
  };

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <Stack gap="md">
        <Text size="sm" fw={600}>
          Document
        </Text>
        <Stack gap="sm">
          <Skeleton height={20} width="60%" radius="sm" />
          <Skeleton height={16} width="100%" radius="sm" />
          <Skeleton height={16} width="90%" radius="sm" />
          <Skeleton height={16} width="80%" radius="sm" />
          <Skeleton height={20} width="50%" radius="sm" />
          <Skeleton height={16} width="100%" radius="sm" />
          <Skeleton height={16} width="70%" radius="sm" />
        </Stack>
      </Stack>
    );
  }

  // -----------------------------------------------------------------------
  // Error state (document region only; rest of page renders normally)
  // -----------------------------------------------------------------------

  if (error) {
    return (
      <Stack gap="md">
        <Text size="sm" fw={600}>
          Document
        </Text>
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="orange"
          variant="light"
        >
          {error}
        </Alert>
      </Stack>
    );
  }

  // -----------------------------------------------------------------------
  // Empty / missing QR state
  // -----------------------------------------------------------------------

  if (!questionnaireResponse) {
    return (
      <Stack gap="md">
        <Text size="sm" fw={600}>
          Document
        </Text>
        <Text size="sm" c="gray.5" ta="center" py="md">
          No document content available.
        </Text>
      </Stack>
    );
  }

  const qrItems = questionnaireResponse.item ?? [];

  // -----------------------------------------------------------------------
  // Main render — item-based, with optional line numbers on every line
  // -----------------------------------------------------------------------

  return (
    <Stack
      gap="md"
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {!hideTitle && (
        <Text size="sm" fw={600}>
          Document
        </Text>
      )}
      <ScrollArea type="scroll" style={{ flex: 1, minHeight: 0 }}>
        {qrItems.length > 0 ? (
          <Stack gap="md">
            {(() => { questionNumberRef.current = 0; return null; })()}
            {qrItems.map((item, idx) => renderQRItem(item, '', idx))}
          </Stack>
        ) : (
          <Text size="sm" c="gray.5" ta="center" py="md">
            Document has no content items.
          </Text>
        )}
      </ScrollArea>
    </Stack>
  );
}
